export interface CrsDefinition { epsg: number; proj4: string }

// proj4 strings for the curated projected systems in constants/epsg.ts. The
// Scene's base map needs the full definition, not the code: the engine
// converts between the scene's grid and the map tiles with it.
const NAMED_DEFS: Record<number, string> = {
  27700: '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs',
  2157: '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=0.99982 +x_0=600000 +y_0=750000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  29903: '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 +a=6377340.189 +b=6356034.447938534 +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs',
  2154: '+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25830: '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25831: '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25832: '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25833: '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  28992: '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs',
  31370: '+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.8686,52.2978,-103.7239,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs',
  3812: '+proj=lcc +lat_1=49.83333333333334 +lat_2=51.16666666666666 +lat_0=50.797815 +lon_0=4.359215833333333 +x_0=649328 +y_0=665262 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  2056: '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs',
  3006: '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  2193: '+proj=tmerc +lat_0=0 +lon_0=173 +k=0.9996 +x_0=1600000 +y_0=10000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  3857: '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs',
};

const utmDefinition = (code: number): string | null => {
  if (code >= 32601 && code <= 32660) return `+proj=utm +zone=${code - 32600} +datum=WGS84 +units=m +no_defs`;
  if (code >= 32701 && code <= 32760) return `+proj=utm +zone=${code - 32700} +south +datum=WGS84 +units=m +no_defs`;
  if (code >= 25828 && code <= 25838) return `+proj=utm +zone=${code - 25800} +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`;
  if (code >= 26901 && code <= 26923) return `+proj=utm +zone=${code - 26900} +datum=NAD83 +units=m +no_defs`;
  return null;
};

export const crsDefinition = (epsg: number | null | undefined): CrsDefinition | null => {
  if (!epsg || !Number.isFinite(epsg)) return null;
  const proj4 = NAMED_DEFS[epsg] ?? utmDefinition(epsg);
  return proj4 ? { epsg, proj4 } : null;
};

export interface FileSrs { epsg?: number | null; proj4?: string | null; projected?: boolean | null }

// A file's own proj4 string is trusted only when it cannot misplace the map:
// PROJ drops the datum shift from most EPSG definitions, and on an ellipsoid
// other than WGS84/GRS80 that leaves the map tens to hundreds of metres off.
export const proj4Usable = (proj4: string | null | undefined): boolean => {
  if (!proj4 || !/\+proj=(?!longlat|latlong|geocent)/.test(proj4)) return false;
  if (/\+nadgrids=(?!@null)/.test(proj4)) return false;
  return /\+towgs84=/.test(proj4) || /\+datum=WGS84\b/.test(proj4) || /\+ellps=(?:WGS84|GRS80)\b/.test(proj4)
    || /\+a=6378137\b.*\+b=6378137\b/.test(proj4);
};

const fromSrs = (srs: FileSrs | null | undefined): CrsDefinition | null => {
  if (!srs || srs.projected === false) return null;
  const known = crsDefinition(srs.epsg);
  if (known) return known;
  if (proj4Usable(srs.proj4)) return { epsg: srs.epsg ?? 0, proj4: srs.proj4! };
  return null;
};

// The grid the Scene draws in: the project's EPSG first, else the CRS a
// ready point cloud carried in its file (stored at conversion; the file
// brings its own definition, so codes the curated list does not know work
// too), else an orthomosaic's — from its stored srs or, for files converted
// before that existed, from the meta.json the engine read when it loaded.
export const sceneCrs = (
  projectEpsg: number | null | undefined,
  files: { type?: string | null; status?: string | null; srs?: FileSrs | null }[],
  loadedOrthos: FileSrs[] = [],
): CrsDefinition | null => {
  const own = crsDefinition(projectEpsg);
  if (own) return own;
  const ready = files.filter((f) => f.status === 'ready');
  const sources = [
    ...ready.filter((f) => f.type === 'point_cloud').map((f) => f.srs),
    ...ready.filter((f) => f.type === 'orthomosaic').map((f) => f.srs),
    ...loadedOrthos,
  ];
  for (const srs of sources) {
    const def = fromSrs(srs);
    if (def) return def;
  }
  return null;
};
